import { api as wiredApi } from './index';

/**
 * A client booking a delivery on InGo's API (the mobile plan's M4 and M5):
 * the services the tariff prices, places from InGo's own map server, the
 * price quoted on the road and held for a quarter of an hour, the booking at
 * that price, and the delivery followed to the door. The client is the
 * token: no id is ever sent for them.
 *
 * The price is the server's. The old screens worked it out in the phone and
 * let the client raise it; a booking now sends the quote it was given, and a
 * quote that no longer holds is refused (`QUOTE_EXPIRED`, `PRICE_CHANGED`,
 * `PINS_CHANGED`), never re-priced silently.
 */
export function createBooking({ api }) {
  return {
    /** `GET quotes/services`: what can be booked today, `[{key, label}]`. */
    async services() {
      const { data } = await api.get('/quotes/services');

      return Array.isArray(data?.services) ? data.services : [];
    },

    /** `GET places/search`: places inside the service area, each with its pin. */
    async searchPlaces(text) {
      const { data } = await api.get('/places/search', { query: { q: text } });

      return Array.isArray(data?.places) ? data.places : [];
    },

    /** `GET places/reverse`: the place at a pin, or null. */
    async reversePlace(latitude, longitude) {
      const { data } = await api.get('/places/reverse', { query: { latitude, longitude } });

      return data?.place ?? null;
    },

    /** `POST quotes`: the price from pickup to drop-off, on the road. */
    async quote(serviceType, pickup, dropoff) {
      const { data } = await api.post('/quotes', {
        service_type: serviceType,
        points: [
          { latitude: pickup.latitude, longitude: pickup.longitude },
          { latitude: dropoff.latitude, longitude: dropoff.longitude },
        ],
      });

      return data.quote;
    },

    /**
     * `POST customer/me/deliveries`: booked at the quote's price, cash on
     * delivery. `pickup` and `dropoff` are the pins the quote was priced on.
     */
    async book({ quote, pickup, dropoff, parcel }) {
      const stop = (pin) => ({
        address: pin.address,
        latitude: pin.latitude,
        longitude: pin.longitude,
        ...(pin.contactName ? { contact_name: pin.contactName } : {}),
        ...(pin.contactPhone ? { contact_phone: pin.contactPhone } : {}),
      });
      const { data } = await api.post('/customer/me/deliveries', {
        quote_id: quote.id,
        total_minor: quote.total_minor,
        payment_method: 'cash',
        pickup: stop(pickup),
        dropoff: stop(dropoff),
        package: {
          size: parcel.size,
          category: parcel.category,
          ...(parcel.category === 'other' && parcel.categoryOther ? { category_other: parcel.categoryOther } : {}),
          ...(parcel.weightKg != null && parcel.weightKg !== '' ? { weight_kg: Number(parcel.weightKg) } : {}),
          ...(parcel.notes ? { notes: parcel.notes } : {}),
        },
      });

      return data;
    },

    /** `GET customer/me/deliveries`: the client's own, newest first (one page). */
    async deliveries() {
      const { data } = await api.get('/customer/me/deliveries');

      return Array.isArray(data) ? data : [];
    },

    /** `GET customer/me/deliveries/{id}`: only the client's own. */
    async delivery(id) {
      const { data } = await api.get(`/customer/me/deliveries/${id}`);

      return data;
    },

    /** `POST customer/me/deliveries/{id}/cancellation`: until a rider has it. */
    async cancel(id, reason = null) {
      const { data } = await api.post(`/customer/me/deliveries/${id}/cancellation`, reason ? { reason } : {});

      return data;
    },
  };
}

export const booking = createBooking({ api: wiredApi });

/** The client's parcel sizes and kinds, their app's words (`PackageDetailsPage.js`), as InGo names them. */
export const PARCEL_SIZES = { Small: 'small', Medium: 'medium', Large: 'large', 'Extra Large': 'xlarge', 'Extra large': 'xlarge' };
export const PARCEL_CATEGORIES = { Documents: 'documents', Electronics: 'electronics', Clothing: 'clothing', Food: 'food', Fragile: 'fragile', Other: 'other' };
